import { Component } from '@verndale/core';
import '../../scss/modules/accordion.scss';
import { eventBus, initialBreakpoint } from '../helpers/resize';
import { BREAKPOINTS } from '../helpers/helpers';
import { enableBodyScroll, disableBodyScroll } from 'body-scroll-lock';
import { createFocusTrap } from 'focus-trap';

function iOS() {
  return (
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
      navigator.userAgent
    ) ||
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
}

class Header extends Component {
  private secondaryOpened: null | HTMLLIElement = null;
  private utilityOpened: null | HTMLLIElement = null;
  private mobile: boolean;
  private light: boolean;
  private scrolled: boolean;
  private visiblePixels = 0;
  // private footer: HTMLElement;
  private focusTrap: null | ReturnType<typeof createFocusTrap>;
  private iOS = iOS();
  private timer: NodeJS.Timer | null = null;

  constructor(el: HTMLElement) {
    super(el);
    // this.footer = document.querySelector('.footer') as HTMLElement;
    this.light = this.el.classList.contains('header--light');
    this.scrolled = window.scrollY > 200;
    this.mobile = window.innerWidth <= BREAKPOINTS.tabletLandscape;

    this.observeFooter();

    this.focusTrap = createFocusTrap(this.el, {
      escapeDeactivates: true,
      clickOutsideDeactivates: true
    });

    this.handleBreakpointChange(initialBreakpoint());
  }

  setupDefaults() {
    this.dom = {
      menuTrigger: this.el.querySelectorAll('.header__trigger'),
      menu: this.el.querySelector<HTMLElement>('.header__mobile'),
      top: this.el.querySelector<HTMLElement>('.header__top'),
      close: this.el.querySelector<HTMLElement>('.header__close'),
      backTrigger: this.el.querySelector<HTMLElement>('.header__back'),
      destinationTrigger: this.el.querySelector<HTMLElement>('.header__primary-trigger-caret'),
      destinationTriggerArrow: this.el.querySelector<HTMLElement>('.header__primary-trigger-arrow'),
      utilityTriggers: this.el.querySelectorAll('.header__utility-caret'),
      utilityLinks: this.el.querySelectorAll('.header__utility-link'),
      utilityTriggersArrow: this.el.querySelectorAll('.header__utility-link--drp-arrow'),
      utilityParentLinks: this.el.querySelectorAll('.header__utility-li>.header__utility-link'),
      utilityPanels: this.el.querySelectorAll('.header__utility-wrapper'),
      utilityNavDesktop: this.el.querySelectorAll('.header__utility-nav'),
      utilityNav: this.el.querySelector<HTMLElement>('.header__utility-list'),
      secondaryTriggers: this.el.querySelectorAll('.header__primary-link'),
      secondaryMenu: this.el.querySelectorAll('.header__primary-li'),
      secondaryPanels: this.el.querySelectorAll('.header__secondary'),
      primaryWrapper: this.el.querySelector<HTMLElement>('.header__primary-list-wrapper'),
      primaryNav: this.el.querySelector<HTMLElement>('.header__primary'),
      bottomBar: this.el.querySelector<HTMLElement>('.header__bottom')
    };

    (this.dom.menu as HTMLElement).style.setProperty('display', 'none');

    (this.dom.secondaryPanels as NodeListOf<HTMLElement>).forEach(secondary => {
      secondary.style.setProperty('display', 'none');
    });

    (this.dom.utilityPanels as NodeListOf<HTMLElement>).forEach(utility => {
      utility.style.setProperty('display', 'none');
    });

    (this.dom.backTrigger as HTMLElement)?.setAttribute('aria-hidden', 'true');
    (this.dom.backTrigger as HTMLElement)?.setAttribute('tabindex', '-1');

    (this.dom.primaryNav as HTMLElement)?.setAttribute('aria-hidden', 'true');
    (this.dom.primaryWrapper as HTMLElement)?.style.setProperty('display', 'none');
  }

  addListeners() {
    (this.dom.menuTrigger as NodeList).forEach(trigger =>
      trigger?.addEventListener('click', this.handleMenuClick.bind(this))
    );

    (this.dom.close as HTMLElement)?.addEventListener(
      'click',
      this.handleDestinationClick.bind(this)
    );

    (this.dom.backTrigger as HTMLElement)?.addEventListener(
      'click',
      this.handleBackClick.bind(this)
    );

    (this.dom.destinationTrigger as HTMLElement)?.addEventListener(
      'mouseover',
      this.handleDestinationClick.bind(this)
    );

    (this.dom.destinationTrigger as HTMLElement)?.addEventListener(
      'focus',
      this.handleDestinationClick.bind(this)
    );

    (this.dom.destinationTrigger as HTMLElement)?.addEventListener(
      'mouseout',
      this.handleDestinationClick.bind(this)
    );

    (this.dom.top as HTMLElement)?.addEventListener(
      'mouseover',
      this.handleDestinationClick.bind(this)
    );

    (this.dom.destinationTriggerArrow as HTMLElement)?.addEventListener(
      'click',
      this.handleDestinationClick.bind(this)
    );

    (this.dom.utilityTriggers as NodeList).forEach(trigger =>
      trigger?.addEventListener('mouseover', this.handleUtilityClick.bind(this))
    );

    (this.dom.utilityTriggers as NodeList).forEach(trigger =>
      trigger?.addEventListener('mouseout', this.handleUtilityClick.bind(this))
    );

    (this.dom.utilityTriggers as NodeList).forEach(trigger =>
      trigger?.addEventListener('focus', this.handleUtilityClick.bind(this))
    );

    (this.dom.utilityLinks as NodeList).forEach(trigger =>
      trigger?.addEventListener('focus', this.handleOtherUtilityFocus.bind(this))
    );

    (this.dom.utilityTriggersArrow as NodeList).forEach(trigger =>
      trigger?.addEventListener('click', this.handleUtilityClick.bind(this))
    );

    (this.dom.utilityNavDesktop as NodeList).forEach(utility =>
      utility?.addEventListener('mouseout', this.handleUtilityClick.bind(this))
    );

    (this.dom.secondaryTriggers as NodeList).forEach((trigger, index) => {
      trigger?.addEventListener('click', this.handleSecondaryClick.bind(this));
      (trigger as HTMLElement).setAttribute('data-index', index.toString());
    });

    (this.dom.secondaryTriggers as NodeList).forEach(trigger =>
      trigger?.addEventListener('focus', this.handleSecondaryHover.bind(this))
    );

    (this.dom.secondaryTriggers as NodeList).forEach(trigger =>
      trigger?.addEventListener('mouseover', this.handleSecondaryHover.bind(this))
    );

    (this.dom.primaryWrapper as HTMLElement)?.addEventListener(
      'transitionend',
      this.handleTransitionEndPrimary.bind(this)
    );

    (this.dom.bottomBar as HTMLElement)?.addEventListener(
      'transitionend',
      this.handleTransitionOffscreen.bind(this)
    );

    (this.dom.utilityNav as HTMLElement)?.addEventListener(
      'transitionend',
      this.handleTransitionEndUtility.bind(this)
    );

    (this.dom.utilityPanels as NodeListOf<HTMLElement>).forEach(panel => {
      panel?.addEventListener('transitionend', e => this.handleTransitionOffscreen(e, true));
    });

    (this.dom.backTrigger as HTMLElement)?.addEventListener('transitionend', e =>
      this.handleTransitionOffscreen(e)
    );

    window.document.addEventListener('mousedown', this.handleMouseClick.bind(this));
    window.document.addEventListener('keydown', this.handleEscKey.bind(this));
    window.document.addEventListener('scroll', this.handleScroll.bind(this));

    eventBus.subscribe('breakpointChange', this.handleBreakpointChange.bind(this));
  }

  handleTransitionEndPrimary(e: TransitionEvent) {
    if (e.propertyName !== 'transform' || e.pseudoElement) return;
    const transform = window.getComputedStyle(e.currentTarget as HTMLElement).transform;
    const matrix = new WebKitCSSMatrix(transform);
    if (matrix.m41 > 0 && this.mobile) {
      (this.dom.primaryWrapper as HTMLElement)?.style.setProperty('display', 'none');
    }

    if ((matrix.m41 === 0 && this.mobile) || (matrix.m42 !== 0 && !this.mobile)) {
      if (!this.mobile) {
        (this.dom.primaryWrapper as HTMLElement)?.style.setProperty('display', 'none');
      }
      (this.dom.secondaryPanels as NodeList).forEach(panel => {
        (panel as HTMLElement).style.setProperty('display', 'none');
      });
    }
  }

  handleTransitionEndUtility(e: Event) {
    const transform = window.getComputedStyle(e.currentTarget as HTMLElement).transform;
    const matrix = new WebKitCSSMatrix(transform);
    if (matrix.m41 === 0 && this.mobile) {
      (this.dom.utilityPanels as NodeList).forEach(panel => {
        if (
          (panel as HTMLElement).style.getPropertyValue('--utility-block-size') === '0px' ||
          !this.mobile
        ) {
          (panel as HTMLElement).style.setProperty('display', 'none');
        }
      });
    }
  }

  handleTransitionOffscreen(e: Event, parent = false) {
    const target = parent
      ? (e.currentTarget as HTMLElement).children[0]
      : (e.currentTarget as HTMLElement);
    const transform = window.getComputedStyle(target as HTMLElement).transform;
    const matrix = new WebKitCSSMatrix(transform);
    if (matrix.m41 !== 0 || matrix.m42 !== 0) {
      (e.currentTarget as HTMLElement)?.style.setProperty('display', 'none');
    }
  }

  handleBreakpointChange(breakpoint: string) {
    this.mobile = breakpoint === 'mobile' || breakpoint === 'tablet';

    if (breakpoint === 'tablet' || breakpoint === 'tabletLandscape') {
      this.el.classList.remove('open');
      this.resetMenu();
    }

    const menu = this.dom.menu as HTMLElement;
    if (this.mobile) {
      menu.style.setProperty('display', 'none');
      menu.setAttribute('aria-expanded', 'false');
      menu.setAttribute('aria-hidden', 'true');
    } else {
      menu.style.setProperty('display', 'flex');
      menu.setAttribute('aria-expanded', 'true');
      menu.setAttribute('aria-hidden', 'false');
    }
  }

  handleEscKey(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      this.resetMenu();
      this.el.classList.remove('open');
    }
  }

  observeFooter() {
    this.el.style.setProperty('--header-bottom-offset', '0px');
  }

  handleIphoneScrollEnd() {
    if (this.timer !== null) {
      clearTimeout(this.timer as NodeJS.Timeout);
    }

    this.timer = setTimeout(() => {
      const bar = this.dom.bottomBar as HTMLElement;
      this.observeFooter();
      if (this.visiblePixels > 0) {
        bar.style.setProperty('transtion', 'opacity 200ms ease-in-out');
        bar.style.setProperty('opacity', '0');
        setTimeout(() => {
          this.observeFooter();
          this.el.style.setProperty('--header-bottom-offset', this.visiblePixels + 'px');
          bar.style.setProperty('opacity', '1');
          setTimeout(() => {
            bar.style.setProperty('transition', 'none');
          }, 200);
        }, 200);
      }
    }, 200);
  }

  handleScroll() {
    if (this.iOS) {
      this.el.style.setProperty('--header-bottom-offset', '0px');
      this.handleIphoneScrollEnd();
    }
    if (!this.light || this.el.classList.contains('open')) return;
    const lightHeader = document.querySelector('light-header-masthead')?.clientHeight || 200;
    if (window.scrollY > lightHeader) {
      this.scrolled = true;
      this.el.classList.remove('header--light');
    } else {
      this.scrolled = false;
      this.el.classList.add('header--light');
    }
  }

  handleMouseClick(e: Event) {
    if (!(e.target as HTMLElement)?.closest('.header')) {
      this.resetMenu();
      this.el.classList.remove('open');
    }
  }

  checkLight(open = false) {
    if (this.light) {
      if (open) {
        this.el.classList.remove('header--light');
      } else {
        if (!this.scrolled && !this.el.classList.contains('submenu')) {
          this.el.classList.add('header--light');
        } else {
          this.el.classList.remove('header--light');
        }
      }
    }
  }

  removeFirstMenuTabIndex() {
    (this.dom.destinationTrigger as HTMLElement).setAttribute('tabindex', '-1');
    (this.dom.utilityTriggers as NodeList).forEach(trigger => {
      (trigger as HTMLElement).setAttribute('tabindex', '-1');
    });
    (this.dom.utilityParentLinks as NodeList).forEach(link => {
      (link as HTMLElement).setAttribute('tabindex', '-1');
    });
  }

  addFirstMenuTabIndex() {
    (this.dom.destinationTrigger as HTMLElement).removeAttribute('tabindex');
    (this.dom.utilityTriggers as NodeList).forEach(trigger => {
      (trigger as HTMLElement).removeAttribute('tabindex');
    });
    (this.dom.utilityParentLinks as NodeList).forEach(link => {
      (link as HTMLElement).removeAttribute('tabindex');
    });
  }

  removeSecondaryTabIndex() {
    (this.dom.secondaryTriggers as NodeList).forEach(trigger => {
      (trigger as HTMLElement).setAttribute('tabindex', '-1');
    });
  }

  addSecondaryTabIndex() {
    (this.dom.secondaryTriggers as NodeList).forEach(trigger => {
      (trigger as HTMLElement).removeAttribute('tabindex');
    });
  }

  openSecondary(secondary: HTMLLIElement) {
    const panel = secondary.querySelector('.header__secondary') as HTMLElement;
    panel?.setAttribute('aria-hidden', 'false');
    panel?.setAttribute('aria-expanded', 'true');
    panel?.style.setProperty('display', 'flex');

    if (this.mobile) {
      (this.dom.backTrigger as HTMLElement).style.display = 'flex';
      (this.dom.backTrigger as HTMLElement).setAttribute('aria-hidden', 'false');
      (this.dom.backTrigger as HTMLElement).removeAttribute('tabindex');
      this.removeFirstMenuTabIndex();
    }

    this.secondaryOpened = secondary;
    window.requestAnimationFrame(() => {
      if (this.mobile) {
        this.el.classList.add('submenu');
        (this.el as HTMLElement)?.classList.add('open-secondary');
      }
      secondary.classList.add('secondary');

      if (document.querySelectorAll('.focus-visible').length > 0 && this.mobile) {
        (this.dom.backTrigger as HTMLElement).focus();
      }
    });
  }

  closeSecondary(displayNone = true) {
    if (this.secondaryOpened) {
      if (this.mobile) {
        (this.dom.bottomBar as HTMLElement).style.setProperty('display', 'flex');
      }
      const panel = this.secondaryOpened.querySelector('.header__secondary') as HTMLElement;
      panel?.setAttribute('aria-hidden', 'true');
      panel?.setAttribute('aria-expanded', 'false');
      if (displayNone) {
        panel?.style.setProperty('display', 'none');
      }
      if (document.querySelectorAll('.focus-visible').length > 0 && this.mobile) {
        (this.dom.backTrigger as HTMLElement).focus;
      }
      this.addSecondaryTabIndex();

      this.secondaryOpened?.classList.remove('secondary');
      this.secondaryOpened = null;
      (this.el as HTMLElement)?.classList.remove('open-secondary');
    }
  }

  openUtility(utility: HTMLLIElement) {
    const panel = utility.querySelector('.header__utility-wrapper') as HTMLElement;
    const list = utility.querySelector('.header__utility-nav') as HTMLElement;
    panel?.setAttribute('aria-hidden', 'false');
    panel?.setAttribute('aria-expanded', 'true');
    panel?.style.setProperty('display', 'flex');
    window.requestAnimationFrame(() => {
      panel.style.setProperty('--utility-block-size', list.clientHeight + 1 + 'px');
      utility.classList.add('utility');
      this.el.classList.add('open-utility');
      this.utilityOpened = utility;
      this.checkLight(true);
    });
  }

  closeUtility(el?: HTMLLIElement) {
    if (this.utilityOpened && !this.mobile) {
      this.utilityOpened.classList.remove('utility');
      const utilityPanel = this.utilityOpened.querySelector(
        '.header__utility-wrapper'
      ) as HTMLElement;
      utilityPanel?.setAttribute('aria-hidden', 'true');
      utilityPanel?.setAttribute('aria-expanded', 'false');
      this.utilityOpened = null;
      this.checkLight();
    } else if (el) {
      el?.classList.remove('utility');
      const panel = el.querySelector('.header__utility-wrapper') as HTMLElement;

      panel?.setAttribute('aria-hidden', 'true');
      panel?.setAttribute('aria-expanded', 'false');
      panel.style.setProperty('--utility-block-size', '0px');
      this.utilityOpened = null;
    }

    this.el.classList.remove('open-utility');
  }

  openDestinationPrimary() {
    const panel = this.el.querySelector('.header__primary') as HTMLElement;
    const dropdown = this.dom.primaryWrapper as HTMLElement;

    panel?.setAttribute('aria-hidden', 'false');
    panel?.setAttribute('aria-expanded', 'true');
    panel?.style.setProperty('display', 'flex');
    dropdown?.style.setProperty('display', 'flex');

    if (this.mobile) {
      panel?.style.setProperty('--dropdown-primary-block-size', dropdown.clientHeight + 1 + 'px');
    } else {
      panel?.style.setProperty('--dropdown-primary-block-size', 'unset');
      this.addSecondaryTabIndex();
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this.el.classList.add('primary');
        this.checkLight(true);
      });
    });
  }

  closeDestinationPrimary() {
    if (this.el.classList.contains('primary')) {
      //destination primary dropdown desktop
      const destinationPanel = this.el.querySelector('.header__primary') as HTMLElement;
      destinationPanel?.setAttribute('aria-hidden', 'true');
      destinationPanel?.setAttribute('aria-expanded', 'false');
      this.el.classList.remove('primary');
      this.el.classList.remove('submenu');
      if (!this.mobile) {
        enableBodyScroll(this.dom.menu as HTMLElement);
        this.checkLight();
      } else {
        //destination primary dropdown mobile
        destinationPanel.style.setProperty('--dropdown-primary-block-size', '0px');
        this.focusTrap?.deactivate();
        this.addFirstMenuTabIndex();
        if (document.querySelectorAll('.focus-visible').length > 0) {
          (this.dom.destinationTrigger as HTMLElement).focus();
        }
      }
    }
  }

  resetMenu() {
    this.closeDestinationPrimary();
    this.closeUtility();
    this.closeSecondary(false);
  }

  handleMenuClick(e: Event) {
    e.preventDefault();
    const menu = this.dom.menu as HTMLElement;
    if (this.el.classList.contains('open')) {
      this.resetMenu();
      this.checkLight();
      this.el.classList.remove('open');
      menu.setAttribute('aria-expanded', 'false');
      menu.setAttribute('aria-hidden', 'true');
      enableBodyScroll(this.dom.menu as HTMLElement);
      this.focusTrap?.deactivate();
    } else {
      (this.dom.menu as HTMLElement)?.style.setProperty('display', 'flex');
      this.focusTrap?.activate();
      this.el.classList.add('open');
      this.checkLight(true);
      menu.setAttribute('aria-expanded', 'true');
      menu.setAttribute('aria-hidden', 'false');
      disableBodyScroll(this.dom.menu as HTMLElement, { reserveScrollBarGap: true });
    }
  }

  handleBackClick(e: Event) {
    e.preventDefault();
    if (this.secondaryOpened) {
      this.closeSecondary(false);
      if (this.el.classList.contains('submenu')) {
        this.el.classList.remove('submenu');
        (this.dom.backTrigger as HTMLElement).setAttribute('aria-hidden', 'true');
        (this.dom.backTrigger as HTMLElement).setAttribute('tabindex', '-1');
      }
    }
  }

  handleDestinationClick(e: Event) {
    e.preventDefault();
    const open = this.el.classList.contains('primary');
    const destinationTrigger = e.target === this.dom.destinationTrigger;
    if (
      (e.type === 'focus' && destinationTrigger && !open && !this.mobile) ||
      (e.type === 'mouseover' && destinationTrigger && !open && !this.mobile) ||
      (e.type === 'mouseover' &&
        open &&
        (e.target as HTMLElement)?.classList.contains('header__top') &&
        !this.mobile) ||
      (e.type === 'mouseout' &&
        destinationTrigger &&
        !((e as MouseEvent)?.relatedTarget as HTMLElement)?.closest('.header__primary') &&
        !this.mobile) ||
      (e.type === 'click' && this.mobile)
    ) {
      if (!this.mobile && this.dom.secondaryMenu && !open) {
        const secondary = (this.dom.secondaryMenu as NodeListOf<HTMLLIElement>)[0];
        this.openSecondary(secondary);
      }

      if (open) {
        this.resetMenu();
      } else {
        if (this.utilityOpened) {
          this.closeUtility();
        }
        this.openDestinationPrimary();
      }
    }
  }

  // Menu expand logic
  handleUtilityClick(e: Event) {
    e.preventDefault();
    if (
      (e.type === 'focus' && !this.mobile) ||
      (e.type === 'mouseover' && !this.el.classList.contains('open-utility') && !this.mobile) ||
      (e.type === 'mouseout' &&
        !((e as MouseEvent)?.relatedTarget as HTMLElement)?.closest('.header__utility-nav') &&
        !this.mobile) ||
      (e.type === 'click' && this.mobile)
    ) {
      const trigger = e.currentTarget as HTMLButtonElement;
      const parent = trigger.closest('.header__utility-li');
      if (this.el.classList.contains('primary') && !this.mobile) {
        this.resetMenu();
      }

      if (this.utilityOpened && this.utilityOpened !== parent) {
        if (!this.mobile) {
          this.closeUtility();
        }
        this.openUtility(parent as HTMLLIElement);
      } else if (this.utilityOpened === parent) {
        this.closeUtility(parent as HTMLLIElement);
      } else {
        this.openUtility(parent as HTMLLIElement);
      }
    }
  }

  handleOtherUtilityFocus(e: Event) {
    if (
      !(e.target as HTMLElement).closest('.header__utility-nav') &&
      !this.mobile &&
      (this.utilityOpened || this.el.classList.contains('primary'))
    ) {
      if (this.utilityOpened) {
        this.closeUtility();
      } else {
        this.closeDestinationPrimary();
      }
    }
  }

  handleSecondaryClick(e: Event) {
    if (this.mobile) {
      e.preventDefault();
    }
    const trigger = e.currentTarget as HTMLButtonElement;
    const parent = trigger.closest('.header__primary-li');

    if (this.secondaryOpened) {
      this.closeSecondary();
    }

    this.openSecondary(parent as HTMLLIElement);
  }

  handleSecondaryHover(e: Event) {
    if (!this.mobile) {
      e.preventDefault();
      const index = (e.currentTarget as HTMLElement)?.dataset.index;
      if (index) {
        if (this.secondaryOpened?.classList.contains('secondary')) {
          this.closeSecondary();
        }

        const secondaryHover = (this.dom.secondaryMenu as NodeListOf<HTMLLIElement>)[
          parseInt(index)
        ];
        this.openSecondary(secondaryHover);
      }
    }
  }
}

export default Header;
